import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import type { EventProps } from 'react-big-calendar'
import moment from 'moment'
import { useClientOptions, EventPreviewPopper, Typography, useUsers, useIsMobile, useDialogs } from '@paintscout/ui'
import type { CalendarEvent } from 'paintscout'
import { useCalendar } from '../CalendarContext'
import { useCancelEventFlow } from 'src/hooks'
import { EventDialog } from '../../../dialogs'
import classnames from 'classnames'
import { formatName, getTimezone } from '@paintscout/util/builder'
import ReactDOM from 'react-dom'
import { getEventLabel } from '@paintscout/util'

const useStyles = makeStyles<Theme, { miniEvent?: boolean }>((theme) => ({
  root: {
    height: '100%',
    borderRadius: theme.borderRadius.md,
    padding: ({ miniEvent }) => (miniEvent ? theme.spacing(0, 0.5) : theme.spacing(0.5)),
    [theme.breakpoints.down('sm')]: {
      '& p': {
        fontSize: theme.typography.pxToRem(12) + ' !important'
      }
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.25)
    }
  },
  unavailable: {
    backgroundColor: theme.paintscout.palette?.brand?.Neutral?.P94,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative', // Needed for pseudo-element
    overflow: 'visible',
    zIndex: 999,
    cursor: 'default',
    backgroundImage: `
    repeating-linear-gradient(
      135deg,
      ${theme.paintscout.palette?.brand?.Neutral?.P98},
      ${theme.paintscout.palette?.brand?.Neutral?.P98} 10px,
      ${theme.paintscout.palette?.brand?.Neutral?.P94} 10px,
      ${theme.paintscout.palette?.brand?.Neutral?.P94} 12px
    )
  `,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      backdropFilter: 'blur(1px)', // Adjust blur as needed
      zIndex: 1
    },
    '&:hover $unavailableText': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  unavailableText: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
    color: theme.palette.common.white,
    padding: theme.spacing(1, 2),
    borderRadius: theme.borderRadius.md,
    zIndex: 2000,
    opacity: 1,
    pointerEvents: 'auto',
    transition: 'opacity 0.2s',
    cursor: 'default'
  },
  integrationOverlay: {
    position: 'relative',
    overflow: 'hidden',
    // Add a dashed overlay using a pseudo-element
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: theme.borderRadius.md,
      // Dashed pattern overlay, semi-transparent so text/background show through
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04) 0px,
          rgba(0,0,0,0.04) 10px,
          transparent 10px,
          transparent 13px
        )
      `
    }
  }
}))

export default function WeekEvent(props: EventProps<CalendarEvent>) {
  const isXs = useIsMobile({ xs: true })

  const { hideWeekends, updateEvent, deleteEvent } = useCalendar()
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const { users } = useUsers()
  const { event } = props
  const { start, end, eventType } = event

  const ref = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [hoveredCell, setHoveredCell] = useState(false)
  const [hoveredPopup, setHoveredPopup] = useState(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const miniEvent = moment(end).diff(moment(start), 'minutes') === 30 || isXs
  const classes = useStyles({ miniEvent })

  const { openDialog, dismissDialog } = useDialogs()
  const cancelEventFlow = useCancelEventFlow()

  const hideContent = isXs && !hideWeekends

  const handleEditEvent = () => {
    openDialog(EventDialog, {
      event,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissDialog()
        setAnchorEl(null)
      }
    })
  }

  const handleCancelEvent = (event: CalendarEvent) =>
    cancelEventFlow(event, (evt, template) => {
      deleteEvent(evt, template)
      setAnchorEl(null)
    })

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setAnchorEl(anchorEl ? null : e.currentTarget)
  }

  // Update rect on hover, scroll, and resize
  useEffect(() => {
    if (!(hoveredCell || hoveredPopup)) return
    function updateRect() {
      if (ref.current) setRect(ref.current.getBoundingClientRect())
    }
    updateRect()
    window.addEventListener('scroll', updateRect, true)
    window.addEventListener('resize', updateRect)
    return () => {
      window.removeEventListener('scroll', updateRect, true)
      window.removeEventListener('resize', updateRect)
    }
  }, [hoveredCell, hoveredPopup])

  if (eventType === '_unavailable_') {
    const showPopup = hoveredCell || hoveredPopup

    const calendar = document.querySelector('.rbc-time-content') as HTMLElement
    const calendarRect = calendar?.getBoundingClientRect()
    const eventRect = ref.current?.getBoundingClientRect()

    let left = eventRect ? eventRect.left + eventRect.width / 2 : 0
    let top = eventRect ? eventRect.top + eventRect.height / 2 : 0

    // Clamp left and top to calendar bounds
    left = Math.max(calendarRect?.left + 24, Math.min(left, calendarRect?.right - 24))
    top = Math.max(calendarRect?.top + 24, Math.min(top, calendarRect?.bottom - 24))

    return (
      <>
        <div
          ref={ref}
          className={classnames(classes.root, classes.unavailable, 'rbc-event-unavailable')}
          style={{ minHeight: 40 }}
          onClick={() => {
            setHoveredCell(true)
            setTimeout(() => {
              setHoveredCell(false)
            }, 1000) // Allow tap to register
          }} // Add this for mobile tap
          onMouseEnter={() => setHoveredCell(true)}
          onMouseLeave={() => setHoveredCell(false)}
        />
        {showPopup &&
          rect &&
          ReactDOM.createPortal(
            <div
              className={classes.unavailableText}
              style={{
                position: 'fixed',
                left,
                top,
                transform: 'translate(-50%, -50%)',
                opacity: 1,
                pointerEvents: 'auto',
                zIndex: 2000
              }}
              onMouseEnter={() => setHoveredPopup(true)}
              onMouseLeave={() => setHoveredPopup(false)}
            >
              <Typography variant="body2" weight={500}>
                Unavailable
              </Typography>
            </div>,
            document.body
          )}
      </>
    )
  }

  const WeekEventContent = () => {
    if (event.eventType === '_google-calendar_') {
      const title = event.title || 'Event'
      return (
        <>
          {!event.allDay && (
            <Typography variant="body2" weight={600} style={{ position: 'relative', zIndex: 2 }}>
              {title}
            </Typography>
          )}
          <Typography
            variant="body2"
            style={{ position: 'relative', zIndex: 2, fontSize: event.allDay ? undefined : 12 }}
          >
            {!miniEvent &&
              getEventLabel(moment(event.start).tz(timezone), moment(event.end).tz(timezone), event.allDay)}
            {event.allDay ? event.title : ''}
          </Typography>
        </>
      )
    } else {
      return (
        <>
          {event.contact && (
            <Typography variant="body2" weight={600}>
              {formatName(event.contact)}
            </Typography>
          )}
          {!miniEvent && (
            <Typography variant="body2" style={{ fontSize: 12 }}>
              {getEventLabel(moment(event.start).tz(timezone), moment(event.end).tz(timezone), event.allDay)}
            </Typography>
          )}
        </>
      )
    }
  }

  return (
    <>
      <div
        className={classnames(
          classes.root,
          event.eventType === '_google-calendar_' ? classes.integrationOverlay : null,
          event.eventType === '_google-calendar_' ? 'rbc-event-imported' : null
        )}
        onClick={handleClick}
        style={{ backgroundColor: event.estimator.color }}
      >
        {!hideContent && <WeekEventContent />}
      </div>
      <EventPreviewPopper
        event={event}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        users={users}
        onClose={() => setAnchorEl(null)}
        onEditEvent={handleEditEvent}
        onCancelEvent={handleCancelEvent}
        minWidth={330}
      />
    </>
  )
}
